/**
 * Phase 2C.M v2 — Écran "Mon compte" (unifié admin / commercial / secrétaire).
 *
 * Contenu : uniquement la card Google Calendar (le toggle thème a été déplacé
 * dans le menu avatar / UserMenu inline — accessible partout sans changer
 * d'écran).
 *
 * Accès : tous les rôles via le user-menu en haut à droite. Route protégée
 * par `ProtectedRoute` sans `adminOnly`.
 *
 * Pas d'OAuth GCal à porter : le token est déjà stocké par utilisateur
 * (collection Mongo `google_calendar_connections`, clé `user_id`) et tous
 * les endpoints `/integrations/google/*` sont `Depends(get_current_user)`.
 */
import { Calendar as CalendarIcon, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL } from "@/lib/roles";
import { Card, CardContent } from "@/components/ui/card";
import GoogleCalendarCard from "@/components/settings/GoogleCalendarCard";

export default function AccountPage() {
  const { state } = useAuth();
  if (!state) return null;
  const { user } = state;

  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-24 md:pb-8" data-testid="account-page">
      {/* Header */}
      <header>
        <h1 className="text-2xl font-semibold text-platform-heading">Mon compte</h1>
        <p className="mt-1 text-sm text-slate-400">
          Connecte tes services externes personnels. Le mode clair/sombre se
          bascule directement depuis le menu de ton avatar en haut à droite.
        </p>
      </header>

      {/* Identité */}
      <section data-testid="account-identity-section">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">
          Identité
        </h2>
        <Card className="border-slate-800 bg-slate-900/40">
          <CardContent className="flex items-start gap-3 p-4">
            <div className="mt-0.5 rounded-full bg-[#C4553B]/15 p-2 text-[#C4553B]">
              <UserIcon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-base font-semibold text-slate-100" data-testid="account-user-name">
                {user.first_name} {user.last_name}
              </div>
              <div className="text-sm text-slate-400" data-testid="account-user-email">
                {user.email}
              </div>
              <div className="mt-1 inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-xs text-slate-300" data-testid="account-user-role">
                {ROLE_LABEL[user.role] || user.role}
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Google Calendar personnel */}
      <section data-testid="account-google-calendar-section">
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-400">
          <span className="inline-flex items-center gap-2">
            <CalendarIcon className="h-3.5 w-3.5" />
            Agenda personnel
          </span>
        </h2>
        <p className="mb-3 text-xs text-slate-500">
          Chaque utilisateur connecte son propre compte Google. Les RDV Wérko
          qui te sont assignés remontent uniquement dans ton agenda personnel.
        </p>
        <GoogleCalendarCard />
      </section>
    </div>
  );
}
